import { BASE_URI, BASE_URI_LC } from '../../constants/ApiConstants';
import { http } from '../http';
import { registerMutation, registerQuery, useMutation, useQuery } from '../../utils/DataFetchingClient';
import { deleteContentProgressFromCache, setContentProgressStatusCache } from '../userProgress';
import { useFullLesson } from '../lessons/lessonQueries';
import { useCallback, useMemo } from 'react';
const ASSETS_URI = `${BASE_URI}/assets`;
const EXERCISES_URI = `${BASE_URI}/exercises`;
const GET_PRACTICAL_EXERCISE_REQUIREMENTS = registerQuery({
  fieldName: 'practicalExerciseRequirements',
  args: ['contentId'],

  fetcher({
    contentId
  }) {
    return http.get(`${BASE_URI}/exercises/requirements/${contentId}`);
  }

});
export const usePracticalExerciseRequirements = contentId => {
  const {
    data: practicalExerciseRequirements,
    loading: practicalExerciseRequirementsLoading,
    error: practicalExerciseRequirementsError
  } = useQuery(GET_PRACTICAL_EXERCISE_REQUIREMENTS, {
    variables: {
      contentId
    }
  });
  return {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  };
};
export const usePracticalExercise = (lessonId, contentId) => {
  const {
    lesson,
    lessonLoading,
    lessonError
  } = useFullLesson(lessonId);
  const practicalExercise = useMemo(() => {
    if (!lesson || !lesson.content) return null;
    const foundExercise = lesson.content.find(content => content.id === contentId);
    return foundExercise;
  }, [contentId, lesson]);
  return {
    practicalExercise,
    practicalExerciseLoading: lessonLoading,
    practicalExerciseError: lessonError
  };
};
const START_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'setPracticalExerciseStarted',
  args: ['contentId', 'trackId', 'lessonId'],

  fetcher({
    contentId,
    lessonId,
    trackId
  }) {
    return http.post(`${BASE_URI_LC}/exercise-submissions/${contentId}/asset/start`, {
      data: {
        trackId,
        lessonId
      }
    });
  }

});
export const useSetPracticalExerciseStarted = () => {
  const [mutate, {
    loading: startPracticalExerciseLoading,
    called: startPracticalExerciseCalled,
    error: startPracticalExerciseError
  }] = useMutation(START_PRACTICAL_EXERCISE);
  const startExercise = useCallback((trackId, lessonId, contentId) => {
    return mutate({
      variables: {
        contentId,
        lessonId,
        trackId
      },

      update() {
        setContentProgressStatusCache(contentId, 'STARTED');
      }

    }).then(result => result.data);
  }, [mutate]);
  return [startExercise, {
    startPracticalExerciseLoading,
    startPracticalExerciseCalled,
    startPracticalExerciseError
  }];
};
const DELETE_PRACTICAL_EXERCISE_PROGRESS = registerMutation({
  fieldName: 'deletePracticalExerciseProgress',
  args: ['contentId'],

  fetcher({
    contentId
  }) {
    return http.delete(`${ASSETS_URI}/exercise/${contentId}/progress`);
  }

});
export const useDeletePracticalExerciseProgress = () => {
  const [mutate, {
    loading: deletePracticalExerciseProgressLoading,
    called: deletePracticalExerciseProgressCalled,
    error: deletePracticalExerciseProgressError
  }] = useMutation(DELETE_PRACTICAL_EXERCISE_PROGRESS);
  const deleteExerciseProgress = useCallback(contentId => {
    return mutate({
      variables: {
        contentId
      },

      update() {
        deleteContentProgressFromCache(contentId);
      }

    }).then(response => response.data);
  }, [mutate]);
  return [deleteExerciseProgress, {
    deletePracticalExerciseProgressLoading,
    deletePracticalExerciseProgressCalled,
    deletePracticalExerciseProgressError
  }];
};
const SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'submitManuallyGradedPracticalExercise',
  args: ['contentId', 'language'],

  fetcher({
    contentId,
    language
  }) {
    return http.post(`${ASSETS_URI}/${contentId}/submission/${language}`);
  }

});
export const useSetManuallyGradedPracticalExerciseSubmitted = () => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseLoading,
    called: submitManuallyGradedPracticalExerciseCalled,
    error: submitManuallyGradedPracticalExerciseError
  }] = useMutation(SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE);
  const submitManuallyGradedExercise = useCallback((contentId, language) => {
    return mutate({
      variables: {
        contentId,
        language
      },

      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW');
      }

    }).then(response => response.data);
  }, [mutate]);
  return [submitManuallyGradedExercise, {
    submitManuallyGradedPracticalExerciseLoading,
    submitManuallyGradedPracticalExerciseCalled,
    submitManuallyGradedPracticalExerciseError
  }];
};
const SUBMIT_AUTOMATICALLY_GRADED_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'submitAutomaticallyGradedPracticalExercise',
  args: ['contentId'],

  fetcher({
    contentId
  }) {
    return http.post(`${EXERCISES_URI}/submission/automatic/${contentId}`);
  }

});
export const useSetAutomaticallyGradedPracticalExerciseSubmitted = () => {
  const [mutate, {
    loading: submitAutomaticallyGradedPracticalExerciseLoading,
    called: submitAutomaticallyGradedPracticalExerciseCalled,
    error: submitAutomaticallyGradedPracticalExerciseError
  }] = useMutation(SUBMIT_AUTOMATICALLY_GRADED_PRACTICAL_EXERCISE);
  const submitAutomaticallyGradedExercise = useCallback(contentId => {
    return mutate({
      variables: {
        contentId
      },

      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW');
      }

    }).then(response => response.data);
  }, [mutate]);
  return [submitAutomaticallyGradedExercise, {
    submitAutomaticallyGradedPracticalExerciseLoading,
    submitAutomaticallyGradedPracticalExerciseCalled,
    submitAutomaticallyGradedPracticalExerciseError
  }];
};
const SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE_WITH_ASSET = registerMutation({
  fieldName: 'submitManuallyGradedPracticalExerciseWithAsset',
  args: ['contentId', 'assetType', 'reference'],

  fetcher({
    contentId,
    assetType,
    reference
  }) {
    return http.post(`${EXERCISES_URI}/submission/manual-with-asset/${contentId}`, {
      data: {
        assetType,
        reference
      }
    });
  }

});
export const useSetManuallyGradedPracticalExerciseWithAssetSubmitted = () => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseWithAssetLoading,
    called: submitManuallyGradedPracticalExerciseWithAssetCalled,
    error: submitManuallyGradedPracticalExerciseWithAssetError
  }] = useMutation(SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE_WITH_ASSET);
  const submitPracticalExerciseWithAsset = useCallback((contentId, assetType, reference) => {
    return mutate({
      variables: {
        contentId,
        assetType,
        reference
      },

      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW');
      }

    }).then(response => response.data);
  }, [mutate]);
  return [submitPracticalExerciseWithAsset, {
    submitManuallyGradedPracticalExerciseWithAssetLoading,
    submitManuallyGradedPracticalExerciseWithAssetCalled,
    submitManuallyGradedPracticalExerciseWithAssetError
  }];
};